import { useMemo, useRef, useState } from 'react';
import { EMAIL_TEMPLATE_AREAS, type EmailTemplateArea, type EmailTemplateSummary } from '@immo/api-contract';
import { ActionsMenu, type ActionsMenuEntry } from '@immo/components';
import { Skeleton } from '@immo/ui-kit';
import { ChevronDownIcon, FileIcon } from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { TOOLBAR_INSERT_ACTION_CLASS } from './composer-body-editor';

type TemplatesState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'loaded'; readonly templates: ReadonlyArray<EmailTemplateSummary> };

export type TemplateMenuProps = {
  readonly workspaceId: string;
  readonly onPick: (emailTemplateId: string) => void;
  readonly area?: EmailTemplateArea;
};

export function TemplateMenu({ workspaceId, onPick, area }: TemplateMenuProps) {
  const t = useTranslations('pages.workspace.mailboxComposer');
  const tRoot = useTranslations();
  const collator = useMemo(() => new Intl.Collator('de', { sensitivity: 'base' }), []);
  const [state, setState] = useState<TemplatesState>({ kind: 'loading' });
  const requestSequence = useRef(0);

  // Every open reloads, so a Vorlage saved meanwhile appears; a slower earlier answer is dropped.
  const load = async () => {
    const sequence = ++requestSequence.current;

    setState({ kind: 'loading' });

    let next: TemplatesState;

    try {
      const result = await apiClient.emailTemplates.list({ pathParams: { workspaceId } });

      next = result.ok ? { kind: 'loaded', templates: result.body.data } : { kind: 'failed' };
    } catch {
      next = { kind: 'failed' };
    }

    if (sequence === requestSequence.current) setState(next);
  };

  const items = useMemo<ReadonlyArray<ActionsMenuEntry>>(() => {
    if (state.kind !== 'loaded') return [];

    return (area === undefined ? EMAIL_TEMPLATE_AREAS : [area]).flatMap((candidate) => {
      const inArea = state.templates
        .filter((template) => template.area === candidate)
        .sort((a, b) => collator.compare(a.name, b.name));

      if (inArea.length === 0) return [];

      return [
        { key: `heading-${candidate}`, heading: tRoot(`common.email-templates.areas.${candidate}`) },
        ...inArea.map((template) => ({
          key: template.id,
          label: template.name,
          onSelect: () => onPick(template.id),
        })),
      ];
    });
  }, [state, area, collator, tRoot, onPick]);

  const emptyLine = () => {
    if (state.kind === 'failed') return t('templates-failed');

    if (area !== undefined && state.kind === 'loaded' && state.templates.length > 0) {
      return t('templates-empty-area', { area: tRoot(`common.email-templates.areas.${area}`) });
    }

    return t('templates-empty');
  };

  const emptyContent =
    state.kind === 'loading' ? (
      <div aria-hidden>
        <Skeleton className="mx-3 my-2 h-4" />
        <Skeleton className="mx-3 my-2 h-4" />
        <Skeleton className="mx-3 my-2 h-4" />
      </div>
    ) : (
      <p className="px-3 py-2.5 text-base-sm text-text-secondary">{emptyLine()}</p>
    );

  return (
    <ActionsMenu
      items={items}
      density="compact"
      menuClassName="w-[340px]"
      triggerLabel={t('insert-template')}
      triggerClassName={TOOLBAR_INSERT_ACTION_CLASS}
      triggerContent={
        <>
          <FileIcon className="h-4 w-4" aria-hidden />
          {t('insert-template')}
          <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden />
        </>
      }
      onOpen={() => void load()}
      emptyContent={emptyContent}
    />
  );
}
